import React from 'react'
import isEmpty from 'lodash/isEmpty'
import { Dropdown } from 'react-bootstrap'
import type { PivotLayout, PivotLayoutMeasure } from '@ztechapps/common.contract'
import type { PivotData } from '../pivot/types'
import { PivotChartTypeEnum } from '@ztechapps/common.contract'
import { BusyButton } from '@ztechapps/webui.button'
import { HorizontalStack, Icon, ShowIf } from '@ztechapps/webui.util'
import { PivotFieldsModal } from '../FieldPanel/PivotFieldsModal'
import { PIVOT_CHART_TYPE_ICONS, PIVOT_CHART_TYPE_LABELS } from '../pivot/constants'
import { PivotFormatModal } from './PivotFormatModal'
import { PivotOptionsModal } from './PivotOptionsModal'

export type PivotToolbarProps = {
  onOpen: () => void
  onSave: () => void
  isLoading: boolean
  hasGrid: boolean
  hasCollapsedRow: boolean
  onExpandAll: () => void
  onCollapseAll: () => void
  onExportExcel: () => void
  onExportCsv: () => void
  onPrint: () => void
  hasChart: boolean
  onExportChartPng: () => void
  onExportChartPdf: () => void
  data: PivotData
  layout: PivotLayout
  shownMeasures: PivotLayoutMeasure[]
  timeZone: string
  onApplyLayout: (layout: PivotLayout) => void
  isFullscreen: boolean
  onToggleFullscreen: () => void
  chartType: PivotChartTypeEnum
  onChangeChartType: (chartType: PivotChartTypeEnum) => void
}

export const PivotToolbar = ({
  onOpen,
  onSave,
  isLoading,
  hasGrid,
  hasCollapsedRow,
  onExpandAll,
  onCollapseAll,
  onExportExcel,
  onExportCsv,
  onPrint,
  hasChart,
  onExportChartPng,
  onExportChartPdf,
  data,
  layout,
  shownMeasures,
  timeZone,
  onApplyLayout,
  isFullscreen,
  onToggleFullscreen,
  chartType,
  onChangeChartType,
}: PivotToolbarProps) => {
  return (
    // one right-aligned cluster: file actions, then view actions, so Open/Save/Export sit beside Collapse all
    // and the filter chips take the rest of the row
    <HorizontalStack gap={2} className="flex-wrap justify-content-end ms-auto">
      <div role="group" aria-label="Pivot file actions" className="btn-group btn-group-sm">
        {/* the data is still loading — nothing to open, save or edit yet, so these hide with the exports below */}
        <ShowIf condition={!isLoading}>
          <BusyButton icon="folder-open" text="Open" onClick={onOpen} />
          <BusyButton icon="save" text="Save" onClick={onSave} />
        </ShowIf>
        {/* nothing to export before a grid shows — hidden, not disabled
          with no reason (webui-component-usage-guide.md → "Blocked control") */}
        <ShowIf condition={hasGrid}>
          <Dropdown className="btn-group">
            <Dropdown.Toggle size="sm">
              <span aria-hidden="true">
                <Icon name="file-export" />
              </span>{' '}
              Export
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={onExportExcel}>
                <span aria-hidden="true">
                  <Icon name="file-excel" />
                </span>{' '}
                Excel
              </Dropdown.Item>
              <Dropdown.Item onClick={onExportCsv}>
                <span aria-hidden="true">
                  <Icon name="file-csv" />
                </span>{' '}
                CSV
              </Dropdown.Item>
              <Dropdown.Item onClick={onPrint}>
                <span aria-hidden="true">
                  <Icon name="print" />
                </span>{' '}
                Print
              </Dropdown.Item>
              <ShowIf condition={chartType !== PivotChartTypeEnum.Grid && hasChart}>
                <Dropdown.Divider />
                <Dropdown.Item onClick={onExportChartPng}>
                  <span aria-hidden="true">
                    <Icon name="image" />
                  </span>{' '}
                  Export chart as PNG
                </Dropdown.Item>
                <Dropdown.Item onClick={onExportChartPdf}>
                  <span aria-hidden="true">
                    <Icon name="file-pdf" />
                  </span>{' '}
                  Export chart as PDF
                </Dropdown.Item>
              </ShowIf>
            </Dropdown.Menu>
          </Dropdown>
        </ShowIf>
      </div>
      <div role="group" aria-label="Pivot view actions" className="btn-group btn-group-sm">
        {/* nothing to expand, collapse or format before a grid shows — hidden, not disabled (see above) */}
        <ShowIf condition={hasGrid}>
          <BusyButton
            icon={hasCollapsedRow ? 'plus-square' : 'minus-square'}
            text={hasCollapsedRow ? 'Expand all' : 'Collapse all'}
            onClick={hasCollapsedRow ? onExpandAll : onCollapseAll}
          />
        </ShowIf>
        {/* the chart type is a view choice, so it leads the view group and Export stays beside Collapse all */}
        <ShowIf condition={hasGrid}>
          <Dropdown className="btn-group">
            <Dropdown.Toggle size="sm">
              <span aria-hidden="true">
                <Icon name={PIVOT_CHART_TYPE_ICONS[chartType]} />
              </span>{' '}
              {PIVOT_CHART_TYPE_LABELS[chartType]}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {Object.values(PivotChartTypeEnum).map((type) => {
                // a column line needs a measure for the bars and another for the line
                const isDisabled = type === PivotChartTypeEnum.ColumnLine && shownMeasures.length < 2
                return (
                  <Dropdown.Item
                    key={type}
                    as="button"
                    active={type === chartType}
                    disabled={isDisabled}
                    onClick={() => onChangeChartType(type)}
                  >
                    <span aria-hidden="true">
                      <Icon name={type === chartType ? 'check' : PIVOT_CHART_TYPE_ICONS[type]} />
                    </span>{' '}
                    {PIVOT_CHART_TYPE_LABELS[type]}
                    {isDisabled ? <small> (needs 2 values)</small> : null}
                  </Dropdown.Item>
                )
              })}
            </Dropdown.Menu>
          </Dropdown>
        </ShowIf>
        <ShowIf condition={hasGrid && !isEmpty(shownMeasures)}>
          <PivotFormatModal
            measures={layout.measures}
            shownMeasures={shownMeasures}
            onApply={(measures) => onApplyLayout({ ...layout, measures })}
          />
        </ShowIf>
        <ShowIf condition={!isLoading}>
          <PivotOptionsModal
            showSubtotals={layout.showSubtotals}
            showGrandTotals={layout.showGrandTotals}
            onApply={(options) => onApplyLayout({ ...layout, ...options })}
          />
          <PivotFieldsModal data={data} layout={layout} timeZone={timeZone} onApply={onApplyLayout} />
        </ShowIf>
        {/* a refresh reloads while fullscreen — the exit button stays so Esc is not the only way out */}
        <ShowIf condition={!isLoading || isFullscreen}>
          <BusyButton
            icon={isFullscreen ? 'compress' : 'expand'}
            text={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            onClick={onToggleFullscreen}
          />
        </ShowIf>
      </div>
    </HorizontalStack>
  )
}
